import { deleteDB, openDB } from "idb";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { learnContentRecords, type LearnContentRecord } from "./contentRepository.js";

const databaseName = "safesphere-learn-library";
const storeName = "active-bundle";
const bundleKey = "latest";
const bundledRevision = "2026-10-02-local-1";
let databasePromise: ReturnType<typeof openDB> | undefined;

export interface StoredLearnBundle {
  readonly revision: string;
  readonly savedAt: string;
  readonly records: readonly LearnContentRecord[];
}

async function getLearnBundleDatabase() {
  databasePromise ??= openDB(databaseName, 1, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(storeName)) database.createObjectStore(storeName);
    },
  });
  return databasePromise;
}

function isUsableBundle(value: unknown): value is StoredLearnBundle {
  if (typeof value !== "object" || value === null || !("records" in value)) return false;
  const records = value.records;
  return (
    Array.isArray(records) &&
    records.length === learnContentRecords.length &&
    records.every(
      (record) =>
        typeof record === "object" &&
        record !== null &&
        "metadata" in record &&
        typeof record.metadata === "object" &&
        record.metadata !== null &&
        "slug" in record.metadata &&
        typeof record.metadata.slug === "string",
    )
  );
}

export async function readActiveLearnBundle(): Promise<StoredLearnBundle | undefined> {
  const value = await (await getLearnBundleDatabase()).get(storeName, bundleKey);
  return isUsableBundle(value) ? value : undefined;
}

export async function cacheBundledLearnContent(): Promise<StoredLearnBundle> {
  const bundle: StoredLearnBundle = {
    revision: bundledRevision,
    savedAt: new Date().toISOString(),
    records: learnContentRecords,
  };
  await (await getLearnBundleDatabase()).put(storeName, bundle, bundleKey);
  return bundle;
}

export async function deleteLearnBundleForTests(): Promise<void> {
  if (databasePromise !== undefined) (await databasePromise).close();
  databasePromise = undefined;
  await deleteDB(databaseName);
}

const LearnContentContext = createContext<readonly LearnContentRecord[]>(learnContentRecords);

/** Uses a dedicated database so Learn cache never changes Family Plan or Vault records. */
export function LearnContentBundleProvider({ children }: PropsWithChildren) {
  const [records, setRecords] = useState<readonly LearnContentRecord[]>(learnContentRecords);

  useEffect(() => {
    let mounted = true;
    void readActiveLearnBundle()
      .then(async (stored) => {
        if (stored?.revision === bundledRevision && mounted) setRecords(stored.records);
        if (stored?.revision !== bundledRevision) await cacheBundledLearnContent();
      })
      .catch(() => {
        // A functional offline app-shell copy is already rendered above.
      });
    return () => {
      mounted = false;
    };
  }, []);

  return <LearnContentContext.Provider value={records}>{children}</LearnContentContext.Provider>;
}

export function useLearnContentRecords(): readonly LearnContentRecord[] {
  return useContext(LearnContentContext);
}
